import React, { useState } from "react";
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Stack, 
  IconButton, 
  Menu, 
  MenuItem,
  Box,
  Container,
  Divider,
  alpha,
  Avatar,
  Paper,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeIcon from '@mui/icons-material/Home';
import CollectionsIcon from '@mui/icons-material/Collections';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DiamondIcon from '@mui/icons-material/Diamond';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';

// Styled Components
const GlassPaper = styled(Paper)(({ theme }) => ({
  background: '#000000',
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  boxShadow: `0 10px 30px -10px rgba(0,0,0,0.5)`,
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : alpha('#FFFFFF', 0.85),
  position: 'relative',
  padding: '4px 10px',
  fontSize: '0.65rem',
  fontWeight: active ? 600 : 400,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  transition: 'all 0.2s ease',
  borderRadius: 0,
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: active ? '30%' : 0,
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      width: '30%',
    },
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  letterSpacing: '0.1em',
  fontSize: '1.2rem',
  color: '#FFFFFF',
  lineHeight: 1.2,
}));

const LogoSubtext = styled(Typography)(({ theme }) => ({
  color: alpha('#FFFFFF', 0.5),
  letterSpacing: '0.2em',
  fontSize: '0.5rem',
  fontWeight: 300,
  textTransform: 'uppercase',
}));

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  color: alpha('#FFFFFF', 0.75),
  transition: 'all 0.2s ease',
  padding: 4,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateY(-1px)',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  textDecoration: 'none',
  position: 'relative',
  zIndex: 2,
  flexShrink: 0,
}));

const MobileMenuItem = styled(ListItem)(({ theme, active }) => ({
  padding: '14px 20px',
  borderRadius: 8,
  margin: '4px 12px',
  transition: 'all 0.2s ease',
  backgroundColor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    transform: 'translateX(4px)',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 40,
    color: active ? theme.palette.primary.main : '#FFFFFF',
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.9rem',
    fontWeight: active ? 600 : 400,
    letterSpacing: '0.05em',
    color: active ? theme.palette.primary.main : '#FFFFFF',
  },
}));

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const location = useLocation();
  const [logoError, setLogoError] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // Mock authentication state
  const isAuthenticated = false;
  const user = {
    name: 'John Collector',
    avatar: 'JC',
    tier: 'Platinum Member'
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  // Navigation items
  const navItems = [
    { label: 'HOME', path: '/', icon: <HomeIcon sx={{ fontSize: 20 }} /> },
    { label: 'BUY', path: '/buy', icon: <ShoppingCartIcon sx={{ fontSize: 20 }} /> },
    { label: 'SELL', path: '/sell', icon: <SellIcon sx={{ fontSize: 20 }} /> },
    { label: 'AUCTIONS', path: '/auctions', icon: <AccountBalanceIcon sx={{ fontSize: 20 }} /> },
    { label: 'SERVICES', path: '/services', icon: <DiamondIcon sx={{ fontSize: 20 }} /> },
    { label: 'SYNTHETIC', path: '/synthetic-coins', icon: <PrecisionManufacturingIcon sx={{ fontSize: 20 }} /> },
    { label: 'CONTACT', path: '/contact', icon: <ContactMailIcon sx={{ fontSize: 20 }} /> },
    { label: 'COLLECTION', path: '/collection', icon: <CollectionsIcon sx={{ fontSize: 20 }} /> },
  ];

  const isActive = (path) => location.pathname === path;

  const mobileMenuContent = (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      bgcolor: '#000000',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Mobile Menu Header */}
      <Box sx={{ 
        p: 2.5, 
        borderBottom: `1px solid ${alpha('#C6A75E', 0.15)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#000000',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component="img"
            src={logoError ? "/assets/images/jp/logo.gif" : "/assets/images/jp/logo.gif"}
            alt="Diamond Coin Logo"
            sx={{
              width: 40,
              height: 40,
              objectFit: 'contain',
              filter: 'drop-shadow(0 2px 4px rgba(198,167,94,0.3))',
            }}
          />
          <Box>
            <Typography variant="body1" color="primary.main" sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '0.1em' }}>
              DIAMOND COIN
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), fontSize: '0.6rem', letterSpacing: '0.1em' }}>
              EST. 2005
            </Typography>
          </Box>
        </Box>
        <IconButtonStyled onClick={handleDrawerToggle} sx={{ color: '#FFFFFF' }}>
          <CloseIcon />
        </IconButtonStyled>
      </Box>

      {/* User Section (if authenticated) */}
      {isAuthenticated && (
        <Box sx={{ 
          p: 2.5, 
          borderBottom: `1px solid ${alpha('#C6A75E', 0.15)}`,
          bgcolor: alpha('#C6A75E', 0.05),
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar 
              sx={{ 
                width: 48, 
                height: 48,
                bgcolor: alpha('#C6A75E', 0.15),
                color: 'primary.main',
                fontSize: '1rem',
                fontWeight: 600,
                border: '2px solid',
                borderColor: alpha('#C6A75E', 0.5),
              }}
            >
              {user.avatar}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {user.name}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                <StarIcon sx={{ color: 'primary.main', fontSize: 12 }} />
                <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.7) }}>
                  {user.tier}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}

      {/* Navigation Items */}
      <List sx={{ flex: 1, pt: 2 }}>
        {navItems.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <MobileMenuItem
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              active={isActive(item.path) ? 1 : 0}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
              {isActive(item.path) && (
                <ChevronRightIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              )}
            </MobileMenuItem>
          </motion.div>
        ))}
      </List>

      {/* Auth Buttons Section */}
      {!isAuthenticated && (
        <Box sx={{ 
          p: 2.5, 
          borderTop: `1px solid ${alpha('#C6A75E', 0.15)}`, 
          bgcolor: '#000000',
        }}>
          <Typography variant="caption" sx={{ 
            color: alpha('#FFFFFF', 0.5), 
            letterSpacing: '0.1em', 
            mb: 2, 
            display: 'block', 
            textAlign: 'center',
            fontWeight: 500,
          }}>
            GET STARTED
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button 
              fullWidth 
              variant="outlined" 
              component={Link} 
              to="/login"
              onClick={handleDrawerToggle}
              startIcon={<LoginIcon />}
              sx={{
                borderColor: alpha('#C6A75E', 0.3),
                color: '#FFFFFF',
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                py: 1.2,
                borderRadius: 2,
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: alpha('#C6A75E', 0.05),
                },
              }}
            >
              SIGN IN
            </Button>
            <Button 
              fullWidth 
              variant="contained" 
              component={Link} 
              to="/register"
              onClick={handleDrawerToggle}
              startIcon={<AppRegistrationIcon />}
              sx={{
                background: 'linear-gradient(135deg, #C6A75E 0%, #A58B47 100%)',
                color: '#000000',
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                py: 1.2,
                borderRadius: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #D4B77A 0%, #C6A75E 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(198,167,94,0.3)',
                },
              }}
            >
              JOIN
            </Button>
          </Stack>
        </Box>
      )}

      {/* User Menu for authenticated users */}
      {isAuthenticated && (
        <Box sx={{ 
          p: 2.5, 
          borderTop: `1px solid ${alpha('#C6A75E', 0.15)}`, 
          bgcolor: '#000000',
        }}>
          <Stack spacing={1}>
            <Button 
              fullWidth 
              variant="text"
              sx={{
                color: '#FFFFFF',
                justifyContent: 'flex-start',
                fontSize: '0.85rem',
                py: 1,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha('#C6A75E', 0.05),
                },
              }}
            >
              Dashboard
            </Button>
            <Button 
              fullWidth 
              variant="text"
              sx={{
                color: '#FFFFFF',
                justifyContent: 'flex-start',
                fontSize: '0.85rem',
                py: 1,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha('#C6A75E', 0.05),
                },
              }}
            >
              My Collection
            </Button>
            <Button 
              fullWidth 
              variant="text"
              sx={{
                color: '#FFFFFF',
                justifyContent: 'flex-start',
                fontSize: '0.85rem',
                py: 1,
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: alpha('#C6A75E', 0.05),
                },
              }}
            >
              History
            </Button>
            <Divider sx={{ borderColor: alpha('#C6A75E', 0.15), my: 1 }} />
            <Button 
              fullWidth 
              variant="text"
              sx={{
                color: '#f44336',
                justifyContent: 'flex-start',
                fontSize: '0.85rem',
                py: 1,
                '&:hover': {
                  backgroundColor: alpha('#f44336', 0.1),
                },
              }}
            >
              Sign Out
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${alpha('#C6A75E', 0.15)}`,
          transition: 'all 0.3s ease',
          zIndex: 1100,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar 
            disableGutters 
            sx={{ 
              minHeight: { xs: 60, md: 70 },
              px: { xs: 1, sm: 2 }
            }}
          >
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              {/* Logo Section - Always visible */}
              <LogoContainer component={Link} to="/">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                  }}
                >
                  <Box
                    component="img"
                    src={logoError ? "/assets/images/jp/logo.gif" : "/assets/images/jp/logo.gif"}
                    alt="Diamond Coin Logo"
                    onError={() => setLogoError(true)}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 2px 6px rgba(198,167,94,0.3))',
                      backgroundColor: 'transparent',
                    }}
                  />
                </motion.div>
                
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <LogoText variant="h4">
                    DIAMOND
                    <Box component="span" sx={{ 
                      color: '#C6A75E',
                      ml: 0.5,
                      fontWeight: 700,
                    }}>
                      COIN
                    </Box>
                    {/* American Flag after DIAMOND COIN text - Made bigger */}
                    <Box
                      component="img"
                      src="/assets/images/usalogo.jpg"
                      alt="USA Flag"
                      sx={{
                        width: 45,
                        height: 30,
                        objectFit: 'cover',
                        borderRadius: '3px',
                        border: '1.5px solid rgba(255,255,255,0.2)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                        ml: 1.5,
                        verticalAlign: 'middle',
                      }}
                    />
                  </LogoText>
                  <LogoSubtext variant="caption">
                    EST. 2005
                  </LogoSubtext>
                </Box>
              </LogoContainer>

              {/* Desktop Navigation */}
              <Stack 
                direction="row" 
                spacing={0.5} 
                sx={{ 
                  display: { xs: "none", lg: "flex" }, 
                  mx: 'auto',
                  px: 1,
                }}
              >
                {navItems.map((item) => (
                  <NavButton
                    key={item.label}
                    component={Link}
                    to={item.path}
                    active={isActive(item.path) ? 1 : 0}
                    startIcon={item.icon}
                  >
                    {item.label}
                  </NavButton>
                ))}
              </Stack>

              {/* Right Section */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {!isMobile && (
                  <>
                    {isAuthenticated ? (
                      <>
                        <IconButtonStyled onClick={handleUserMenuOpen}>
                          <Avatar 
                            sx={{ 
                              width: 28, 
                              height: 28,
                              bgcolor: alpha('#C6A75E', 0.15),
                              color: 'primary.main',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              border: '1px solid',
                              borderColor: alpha('#C6A75E', 0.3),
                            }}
                          >
                            {user.avatar}
                          </Avatar>
                        </IconButtonStyled>
                        <Menu
                          anchorEl={userMenuAnchor}
                          open={Boolean(userMenuAnchor)}
                          onClose={handleUserMenuClose}
                          PaperProps={{
                            component: GlassPaper,
                            sx: {
                              mt: 1,
                              minWidth: 200,
                              borderRadius: 1,
                            }
                          }}
                        >
                          <Box sx={{ px: 2, py: 1.2, background: alpha('#C6A75E', 0.08) }}>
                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500, fontSize: '0.75rem' }}>
                              {user.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.5), display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, fontSize: '0.6rem' }}>
                              <StarIcon sx={{ color: 'primary.main', fontSize: 10 }} />
                              {user.tier}
                            </Typography>
                          </Box>
                          <Divider sx={{ borderColor: alpha('#C6A75E', 0.15) }} />
                          <MenuItem onClick={handleUserMenuClose} sx={{ py: 1, px: 2, fontSize: '0.7rem', color: alpha('#FFFFFF', 0.9) }}>Dashboard</MenuItem>
                          <MenuItem onClick={handleUserMenuClose} sx={{ py: 1, px: 2, fontSize: '0.7rem', color: alpha('#FFFFFF', 0.9) }}>My Collection</MenuItem>
                          <MenuItem onClick={handleUserMenuClose} sx={{ py: 1, px: 2, fontSize: '0.7rem', color: alpha('#FFFFFF', 0.9) }}>History</MenuItem>
                          <Divider sx={{ borderColor: alpha('#C6A75E', 0.15) }} />
                          <MenuItem onClick={handleUserMenuClose} sx={{ py: 1, px: 2, fontSize: '0.7rem', color: '#f44336' }}>
                            Sign Out
                          </MenuItem>
                        </Menu>
                      </>
                    ) : (
                      <>
                        <Button
                          component={Link}
                          to="/login"
                          variant="text"
                          sx={{ 
                            display: { xs: 'none', md: 'inline-flex' },
                            color: alpha('#FFFFFF', 0.85),
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            letterSpacing: '0.1em',
                            px: 1.5,
                            py: 0.6,
                            minWidth: 'auto',
                            '&:hover': {
                              color: 'primary.main',
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          SIGN IN
                        </Button>
                        <Button
                          component={Link}
                          to="/register"
                          variant="contained"
                          sx={{ 
                            display: { xs: 'none', md: 'inline-flex' },
                            background: 'linear-gradient(135deg, #C6A75E 0%, #A58B47 100%)',
                            color: '#000000',
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            letterSpacing: '0.1em',
                            px: 2,
                            py: 0.6,
                            borderRadius: 20,
                            boxShadow: '0 2px 8px rgba(198,167,94,0.2)',
                            minWidth: 'auto',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #D4B77A 0%, #C6A75E 100%)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 12px rgba(198,167,94,0.3)',
                            },
                          }}
                        >
                          JOIN
                        </Button>
                      </>
                    )}
                  </>
                )}

                {/* Mobile Menu Button */}
                <IconButtonStyled
                  edge="end"
                  aria-label="menu"
                  sx={{ 
                    display: { xs: "flex", lg: "none" },
                    backgroundColor: alpha('#C6A75E', 0.1),
                    padding: '8px',
                    '&:hover': {
                      backgroundColor: alpha('#C6A75E', 0.2),
                    },
                  }}
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButtonStyled>

                {/* Mobile Auth Indicator */}
                {isMobile && !isAuthenticated && (
                  <Button
                    component={Link}
                    to="/login"
                    variant="outlined"
                    size="small"
                    startIcon={<PersonIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      display: { xs: 'flex', lg: 'none' },
                      borderColor: alpha('#C6A75E', 0.3),
                      color: '#FFFFFF',
                      fontSize: '0.7rem',
                      py: 0.5,
                      px: 1.5,
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </Stack>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <SwipeableDrawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        onOpen={() => setMobileOpen(true)}
        swipeAreaWidth={0}
        disableBackdropTransition={false}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: { xs: '85%', sm: '350px' },
            maxWidth: '100%',
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden',
          }
        }}
      >
        {mobileMenuContent}
      </SwipeableDrawer>
    </>
  );
};

export default Navbar;